import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import {
  ParkingZone,
  PortalModule,
  type ParkingAssignmentDto,
  type PersonSummaryDto,
} from '@ciimar/shared';
import { api } from '../api/client';
import { useAccess } from '../auth/AccessContext';
import { useT } from '../lib/i18n';

const ZONE_LABEL_KEYS: Record<ParkingZone, string> = {
  [ParkingZone.Internal]: 'admin.vehicles.zone.internal',
  [ParkingZone.External]: 'admin.vehicles.zone.external',
};

interface FormState {
  id?: string;
  personId: string;
  personLabel: string;
  plate: string;
  vehicle: string;
  zone: ParkingZone;
  location: string;
  cardsText: string;
  notes: string;
  active: boolean;
}

const EMPTY_FORM: FormState = {
  personId: '',
  personLabel: '',
  plate: '',
  vehicle: '',
  zone: ParkingZone.Internal,
  location: '',
  cardsText: '',
  notes: '',
  active: true,
};

/** Split a free-text list of card ids (comma/semicolon/newline separated). */
function parseCards(text: string): string[] {
  return text
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function Vehicles() {
  const t = useT();
  const { canEdit } = useAccess();
  const editable = canEdit(PortalModule.Viaturas);
  const [rows, setRows] = useState<ParkingAssignmentDto[]>([]);
  const [search, setSearch] = useState('');
  const [locations, setLocations] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setRows(await api.listParking(search || undefined));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // The Local is a controlled list managed in Configuração Global.
    api
      .institutionLocations()
      .then((ls) => setLocations(ls.map((l) => l.name)))
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function startCreate() {
    setForm({ ...EMPTY_FORM });
  }
  function startEdit(r: ParkingAssignmentDto) {
    setForm({
      id: r.id,
      personId: r.personId,
      personLabel: [r.personName, r.personCode].filter(Boolean).join(' · ') || r.personId,
      plate: r.plate,
      vehicle: r.vehicle ?? '',
      zone: r.zone,
      location: r.location,
      cardsText: r.cardIds.join(', '),
      notes: r.notes ?? '',
      active: r.active,
    });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!form) return;
    setBusy(true);
    setError(null);
    try {
      const payload = {
        personId: form.personId,
        plate: form.plate,
        vehicle: form.vehicle || null,
        zone: form.zone,
        location: form.location,
        cardIds: parseCards(form.cardsText),
        notes: form.notes || null,
        active: form.active,
      };
      if (form.id) await api.updateParking(form.id, payload);
      else await api.createParking(payload);
      setForm(null);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(r: ParkingAssignmentDto) {
    if (!window.confirm(t('admin.vehicles.confirmDelete', { plate: r.plate }))) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteParking(r.id);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <div className="page-head">
        <div className="page-head-text">
          <h2>{t('admin.vehicles.title')}</h2>
          <p className="page-sub">
            {loading
              ? t('common.loading')
              : rows.length === 1
                ? t('admin.vehicles.countOne', { n: rows.length })
                : t('admin.vehicles.countMany', { n: rows.length })}
          </p>
        </div>
        <div className="spacer" />
        {editable && !form && (
          <button type="button" onClick={startCreate}>
            {t('admin.vehicles.create')}
          </button>
        )}
      </div>

      {error && <div className="alert error">{error}</div>}

      {form && (
        <ParkingForm
          form={form}
          setForm={setForm}
          locations={locations}
          busy={busy}
          onSubmit={submit}
          onCancel={() => setForm(null)}
        />
      )}

      <div className="erp-panel">
        <div className="erp-toolbar">
          <form
            className="erp-toolbar-actions"
            onSubmit={(e) => {
              e.preventDefault();
              void load();
            }}
          >
            <label className="erp-search">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              >
                <path d="M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14M21 21l-5-5" />
              </svg>
              <input
                placeholder={t('admin.vehicles.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            <button type="submit">{t('admin.vehicles.search')}</button>
          </form>
        </div>

        {loading ? (
          <div className="panel-empty">{t('common.loading')}</div>
        ) : rows.length === 0 ? (
          <div className="panel-empty is-empty">{t('admin.vehicles.empty')}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="grid">
              <thead>
                <tr>
                  <th>{t('admin.vehicles.colCollaborator')}</th>
                  <th>{t('admin.vehicles.colPlate')}</th>
                  <th>{t('admin.vehicles.colVehicle')}</th>
                  <th>{t('admin.vehicles.colZone')}</th>
                  <th>{t('admin.vehicles.colLocation')}</th>
                  <th>{t('admin.vehicles.colCards')}</th>
                  <th>{t('admin.vehicles.colStatus')}</th>
                  {editable && <th />}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      {r.personName ?? '—'}
                      {r.personCode && <span className="cell-sub">{r.personCode}</span>}
                    </td>
                    <td>
                      <strong>{r.plate}</strong>
                    </td>
                    <td>{r.vehicle ?? '—'}</td>
                    <td>{t(ZONE_LABEL_KEYS[r.zone])}</td>
                    <td>{r.location}</td>
                    <td>
                      {r.cardIds.length === 0 ? (
                        '—'
                      ) : (
                        <span className="card-chips">
                          {r.cardIds.map((c) => (
                            <span key={c} className="card-chip">
                              {c}
                            </span>
                          ))}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={r.active ? 'badge approved' : 'badge'}>
                        {r.active ? t('admin.vehicles.active') : t('admin.vehicles.inactive')}
                      </span>
                    </td>
                    {editable && (
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button type="button" className="btn-ghost" onClick={() => startEdit(r)}>
                          {t('admin.vehicles.edit')}
                        </button>{' '}
                        <button
                          type="button"
                          className="btn-danger"
                          onClick={() => void remove(r)}
                          disabled={busy}
                        >
                          {t('admin.vehicles.delete')}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

/** The create/edit form (collaborator + vehicle + parking details). */
function ParkingForm({
  form,
  setForm,
  locations,
  busy,
  onSubmit,
  onCancel,
}: {
  form: FormState;
  setForm: (f: FormState) => void;
  locations: string[];
  busy: boolean;
  onSubmit: (e: FormEvent) => void;
  onCancel: () => void;
}) {
  const t = useT();
  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm({ ...form, [key]: value });
  }
  return (
    <form className="erp-panel parking-form" onSubmit={onSubmit} style={{ marginBottom: 16 }}>
      <div className="erp-toolbar">
        <strong>
          {form.id ? t('admin.vehicles.form.editTitle') : t('admin.vehicles.form.createTitle')}
        </strong>
      </div>
      <div className="parking-grid">
        <div className="builder-field builder-wide">
          <span>{t('admin.vehicles.form.collaborator')}</span>
          <PersonField
            personId={form.personId}
            label={form.personLabel}
            onSelect={(p) =>
              setForm({
                ...form,
                personId: p?.id ?? '',
                personLabel: p ? [p.fullName, p.code].filter(Boolean).join(' · ') : '',
              })
            }
          />
        </div>
        <label className="builder-field">
          <span>{t('admin.vehicles.form.plate')}</span>
          <input required value={form.plate} onChange={(e) => set('plate', e.target.value)} />
        </label>
        <label className="builder-field">
          <span>{t('admin.vehicles.form.vehicle')}</span>
          <input value={form.vehicle} onChange={(e) => set('vehicle', e.target.value)} />
        </label>
        <label className="builder-field">
          <span>{t('admin.vehicles.form.zoneLabel')}</span>
          <select
            className="erp-select"
            value={form.zone}
            onChange={(e) => set('zone', e.target.value as ParkingZone)}
          >
            <option value={ParkingZone.Internal}>{t('admin.vehicles.zone.internal')}</option>
            <option value={ParkingZone.External}>{t('admin.vehicles.zone.external')}</option>
          </select>
        </label>
        <label className="builder-field">
          <span>{t('admin.vehicles.form.location')}</span>
          {locations.length > 0 ? (
            <select
              className="erp-select"
              required
              value={form.location}
              onChange={(e) => set('location', e.target.value)}
            >
              <option value="">{t('admin.vehicles.form.locationSelect')}</option>
              {/* Keep an existing value even if it was removed from the list. */}
              {form.location && !locations.includes(form.location) && (
                <option value={form.location}>{form.location}</option>
              )}
              {locations.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          ) : (
            <input
              required
              placeholder={t('admin.vehicles.form.locationPlaceholder')}
              value={form.location}
              onChange={(e) => set('location', e.target.value)}
            />
          )}
        </label>
        <label className="builder-field builder-wide">
          <span>{t('admin.vehicles.form.cards')}</span>
          <input
            value={form.cardsText}
            placeholder={t('admin.vehicles.form.cardsPlaceholder')}
            onChange={(e) => set('cardsText', e.target.value)}
          />
        </label>
        <label className="builder-field builder-wide">
          <span>{t('admin.vehicles.form.notes')}</span>
          <input value={form.notes} onChange={(e) => set('notes', e.target.value)} />
        </label>
        <label className="builder-check">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => set('active', e.target.checked)}
          />
          <span>{t('admin.vehicles.form.active')}</span>
        </label>
      </div>
      <div className="builder-actions">
        <div className="spacer" style={{ flex: 1 }} />
        <button type="button" className="secondary" onClick={onCancel} disabled={busy}>
          {t('admin.vehicles.form.cancel')}
        </button>
        <button type="submit" disabled={busy || !form.personId}>
          {busy ? t('admin.vehicles.form.saveBusy') : t('admin.vehicles.form.save')}
        </button>
      </div>
    </form>
  );
}

/** A debounced collaborator search + select. */
function PersonField({
  personId,
  label,
  onSelect,
}: {
  personId: string;
  label: string;
  onSelect: (p: PersonSummaryDto | null) => void;
}) {
  const t = useT();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PersonSummaryDto[]>([]);

  useEffect(() => {
    const term = query.trim();
    if (personId || term.length < 2) {
      setResults([]);
      return;
    }
    let active = true;
    const h = setTimeout(async () => {
      try {
        const people = await api.listPeople(term);
        if (active) setResults(people.slice(0, 8));
      } catch {
        if (active) setResults([]);
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(h);
    };
  }, [query, personId]);

  if (personId) {
    return (
      <div className="actions-row" style={{ gap: 8 }}>
        <span className="badge approved">{label || personId}</span>
        <button
          type="button"
          className="link-button"
          onClick={() => {
            onSelect(null);
            setQuery('');
          }}
        >
          {t('admin.vehicles.form.change')}
        </button>
      </div>
    );
  }
  return (
    <div style={{ position: 'relative' }}>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('admin.vehicles.form.collaboratorPlaceholder')}
        autoComplete="off"
      />
      {results.length > 0 && (
        <ul className="picker-list">
          {results.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className="picker-item"
                onClick={() => {
                  onSelect(p);
                  setResults([]);
                }}
              >
                <span>{p.fullName}</span>
                <span className="muted">
                  {[p.code, p.professionalEmail].filter(Boolean).join(' · ')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
